/**
 * Der **evcc-Standort** an seiner Gerätedetailseite (HM-165): was gerade gilt —
 * Tarife, Betriebsart des Hausakkus — und was sich einstellen lässt.
 *
 * Gesetzt wird über den Schreib-Endpunkt an den Datenpunkt des Felds, wie an
 * jedem Knoten; die Wertebereiche prüft die API vorab. **Die Vorgabe der
 * Betriebsart hat keinen Wert, den man anzeigen könnte**: evcc meldet sie nicht
 * verlässlich zurück. Sie steht deshalb als Auswahl mit eigenem „Vorgeben" da,
 * ohne eine vorgewählte Zeile, die so täte, als gelte sie — was gilt, sagt die
 * Zeile „Betriebsart des Hausakkus" darüber.
 */
import { useState } from "react";
import { Alert, Button, Card, Select, Switch } from "@hausmeter/ui";
import { useWriteDatapoint } from "./datapoint-write.js";
import { DefinitionList } from "./definition-list.js";
import { Stepper } from "./charge-point.js";
import { SectionHeading } from "./section.js";
import { formatTimeAgo } from "../lib/format.js";
import { useEvccSiteState } from "../lib/queries.js";
import {
  BATTERY_MODE_PRESETS,
  evccSiteRows,
  type BatteryModePreset,
  type EvccSiteState,
} from "../lib/evcc-site.js";

const PRESET_CODES: Readonly<Record<BatteryModePreset, number>> = {
  none: 0,
  normal: 1,
  hold: 2,
  charge: 3,
  hold_charge: 4,
  discharge_grid: 5,
};

const GRID = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 230px), 1fr))",
  gap: "var(--sp-5)",
  alignItems: "start",
} as const;
const LABEL = { fontSize: 13, fontWeight: 600, color: "var(--text-body)" } as const;
const HINT = { fontSize: 12, lineHeight: 1.45, color: "var(--text-muted)" } as const;

type Field = { value: number | null; datapointId: string | null; writable: boolean };
type Flag = { value: boolean | null; datapointId: string | null; writable: boolean };

const settable = (field: { datapointId: string | null; writable: boolean }) =>
  field.datapointId !== null && field.writable;

export function EvccSiteSection({ deviceId, canWrite }: { deviceId: string; canWrite: boolean }) {
  const state = useEvccSiteState(deviceId);
  const write = useWriteDatapoint(deviceId);
  const [error, setError] = useState<string | null>(null);
  const [preset, setPreset] = useState<BatteryModePreset>("none");

  if (state.isPending) return <Card padding="sm">Der Standort wird geladen …</Card>;
  if (state.isError || state.data === undefined) {
    return (
      <Alert tone="danger">
        {state.error?.message ?? "Der Standort konnte nicht geladen werden."}
      </Alert>
    );
  }
  const s: EvccSiteState = state.data;
  const rows = evccSiteRows(s);
  const enabled = canWrite && !write.isPending;
  const send = (field: { datapointId: string | null }, value: number) => {
    if (field.datapointId === null) return;
    setError(null);
    write.mutate(
      { datapointId: field.datapointId, value },
      { onError: (cause) => setError(cause.message) },
    );
  };

  const ratio = (label: string, field: Field, hint: string) =>
    settable(field) ? (
      <Stepper
        key={label}
        label={label}
        unit="%"
        field={field}
        toShown={(value) => Math.round(value * 100)}
        fromShown={(percent) => percent / 100}
        min={0}
        max={100}
        step={5}
        enabled={enabled}
        hint={hint}
        onSend={(value) => send(field, value)}
      />
    ) : null;
  const flag = (label: string, field: Flag, text: string) =>
    settable(field) ? (
      <div key={label} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <span style={LABEL}>{label}</span>
        <Switch
          label={text}
          checked={field.value === true}
          disabled={!enabled || field.value === null}
          onChange={(checked) => send(field, checked ? 1 : 0)}
        />
      </div>
    ) : null;
  const priceLimit = (label: string, field: Field, set: Flag, hint: string) =>
    settable(field) ? (
      <div key={label} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <Stepper
          label={label}
          unit="ct/kWh"
          field={field}
          toShown={(eur) => Math.round(eur * 100)}
          fromShown={(ct) => ct / 100}
          min={-100}
          max={100}
          enabled={enabled}
          hint={hint}
          onSend={(value) => send(field, value)}
        />
        {field.value !== null && settable(set) && (
          <div>
            <Button variant="ghost" size="sm" disabled={!enabled} onClick={() => send(set, 0)}>
              Grenze entfernen
            </Button>
          </div>
        )}
      </div>
    ) : null;

  const settings = canWrite
    ? [
        ratio(
          "Hausakku für Ladepunkte ab",
          s.bufferSoc,
          "Über diesem Stand darf der Hausakku für Ladepunkte entladen werden.",
        ),
        ratio(
          "Hausakku startet Laden ab",
          s.bufferStartSoc,
          "Über diesem Stand beginnt Laden mit Sonne auch ohne Überschuss.",
        ),
        ratio(
          "Hausakku hat Vorrang bis",
          s.prioritySoc,
          "Bis zu diesem Stand lädt der Hausakku vor den Ladepunkten.",
        ),
        settable(s.residualPowerW) ? (
          <Stepper
            key="residual"
            label="Regelreserve"
            unit="W"
            field={s.residualPowerW}
            min={-100_000}
            max={100_000}
            step={50}
            enabled={enabled}
            hint="Um diesen Wert regelt evcc am Netzanschluss vorbei — positiv lässt Bezug übrig."
            onSend={(value) => send(s.residualPowerW, value)}
          />
        ) : null,
        settable(s.gridExportLimitW) ? (
          <Stepper
            key="export"
            label="Einspeisegrenze"
            unit="W"
            field={s.gridExportLimitW}
            min={0}
            max={100_000}
            step={100}
            enabled={enabled}
            hint="Mehr gibt die Anlage nicht ins Netz ab."
            onSend={(value) => send(s.gridExportLimitW, value)}
          />
        ) : null,
        flag(
          "Hausakku beim Laden",
          s.batteryDischargeControl,
          "Entladung sperren, solange ein Fahrzeug lädt",
        ),
        flag("Ins Netz entladen", s.batteryGridDischarge, "Hausakku darf ins Netz entladen"),
        priceLimit(
          "Aus dem Netz laden unter",
          s.batteryGridChargeLimitEur,
          s.batteryGridChargeLimitSet,
          "Unter diesem Netzpreis lädt der Hausakku aus dem Netz.",
        ),
        priceLimit(
          "Ins Netz entladen über",
          s.batteryGridDischargeLimitEur,
          s.batteryGridDischargeLimitSet,
          "Über diesem Preis entlädt der Hausakku ins Netz.",
        ),
      ].filter((element) => element !== null)
    : [];
  const withPreset = canWrite && settable(s.batteryModeExternal);

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
      <SectionHeading
        title="Standort"
        subtitle={
          s.measuredAt === null
            ? "Noch kein Wert gemeldet."
            : `Wie evcc das Haus regelt · zuletzt gemeldet ${formatTimeAgo(s.measuredAt)}.`
        }
      />
      <Card>
        {rows.length === 0 ? (
          <p style={{ margin: 0, color: "var(--text-muted)", fontSize: 13 }}>
            Der Standort hat noch keinen seiner verabredeten Werte gemeldet.
          </p>
        ) : (
          <DefinitionList rows={rows} />
        )}
      </Card>
      {(settings.length > 0 || withPreset) && (
        <Card>
          <div style={GRID}>
            {settings}
            {withPreset && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <Select
                  label="Betriebsart vorgeben"
                  value={preset}
                  disabled={!enabled}
                  options={BATTERY_MODE_PRESETS}
                  onChange={(event) => setPreset(event.target.value as BatteryModePreset)}
                />
                <div>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={!enabled}
                    onClick={() => send(s.batteryModeExternal, PRESET_CODES[preset])}
                  >
                    Vorgeben
                  </Button>
                </div>
                <span style={HINT}>
                  evcc meldet die Vorgabe nicht zurück; was gilt, steht oben unter „Betriebsart des
                  Hausakkus“.
                </span>
              </div>
            )}
          </div>
        </Card>
      )}
      {error !== null && <Alert tone="danger">{error}</Alert>}
      {write.isSuccess && error === null && (
        <p style={{ ...HINT, margin: 0 }}>
          Abgeschickt — der neue Wert steht hier, sobald evcc ihn meldet. Die Quittung steht unter
          „Einstellungen“ bei den Aufträgen.
        </p>
      )}
    </section>
  );
}
